/**
 * Shared Playhead helpers for Replay Player HUD and MiniMapPlayer.
 */
import { armyCostForEntities, compositionByKind } from './components/sc2/replayUnitLookup';

export const PLAYER_COLORS = {
  Red: '#c0392b',
  Blue: '#2980b9',
  Teal: '#1abc9c',
  Purple: '#8e44ad',
  Yellow: '#f1c40f',
  Orange: '#e67e22',
  Green: '#27ae60',
  LightPink: '#fd79a8',
  Violet: '#6c5ce7',
  LightGrey: '#b2bec3',
  DarkGreen: '#006266',
  Brown: '#8d6e63',
  LightGreen: '#55efc4',
  DarkGrey: '#636e72',
  Pink: '#e84393',
};

export function colorForPlayer(player) {
  if (!player) return '#95a5a6';
  return PLAYER_COLORS[player.color] || '#95a5a6';
}

export function formatClock(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/** Replay minimap events → live entities at Playhead time t. */
export function entitiesAtTime(events, time) {
  const live = new Map();
  for (const event of events || []) {
    if (event.t > time) break;
    if (event.op === 'add') {
      live.set(event.id, {
        id: event.id,
        pid: event.pid,
        name: event.name,
        kind: event.kind,
        x: event.x,
        y: event.y,
      });
    } else if (event.op === 'move') {
      const existing = live.get(event.id);
      if (existing && event.x != null && event.y != null) {
        existing.x = event.x;
        existing.y = event.y;
      }
    } else if (event.op === 'del') {
      live.delete(event.id);
    }
  }
  return Array.from(live.values());
}

export function rosterForPlayer(entities, player) {
  const owned = (entities || []).filter((e) => e.pid === player.pid);
  const cost = armyCostForEntities(owned);
  return {
    player,
    cost,
    army: compositionByKind(owned, 'army'),
    buildings: compositionByKind(owned, 'building'),
    workers: owned.filter((e) => e.kind === 'worker').length,
  };
}

/** Production Intervals currently in progress at Playhead t. */
export function inProgressIntervals(intervals, time) {
  return (intervals || []).filter(
    (i) =>
      (i.start_real_time || 0) <= time && time < (i.finish_real_time || 0)
  );
}

/** Upgrades completed by Playhead t. */
export function upgradesAtTime(upgrades, time) {
  return (upgrades || []).filter((u) => (u.time || 0) <= time);
}

/** Last stats sample with sample.t <= time (hold last). */
export function statsAtTime(stats, time) {
  if (!stats || stats.length === 0) return null;
  let last = null;
  for (const sample of stats) {
    if (sample.t > time) break;
    last = sample;
  }
  return last;
}

/** Last camera for pid with c.t <= time. */
export function cameraAtTime(cameras, pid, time) {
  if (!cameras || cameras.length === 0 || pid == null) return null;
  let last = null;
  for (const cam of cameras) {
    if (cam.pid !== pid) continue;
    if (cam.t > time) break;
    last = cam;
  }
  return last;
}

/**
 * Remap legacy cameras[] that stored HotS+ CameraEvent uid (incl. pid 0)
 * onto tracker player pids used by minimap players. New parses are unchanged.
 */
export function normalizeCameraPids(cameras, players) {
  if (!cameras || cameras.length === 0 || !players || players.length === 0) {
    return cameras;
  }
  if (!cameras.some((c) => c.pid === 0)) return cameras;
  const sorted = [...players].sort((a, b) => a.pid - b.pid);
  const uidToPid = new Map(sorted.map((p, i) => [i, p.pid]));
  return cameras.map((c) => {
    const mapped = uidToPid.get(c.pid);
    return mapped == null ? c : { ...c, pid: mapped };
  });
}

/**
 * SC2 minimap camera FoV is a perspective frustum → trapezoid on the top-down
 * map. Default camera faces +Y (north): far edge is wider. Canvas Y is flipped
 * (higher world Y → smaller canvas y). Returns corners
 * [farLeft, farRight, nearRight, nearLeft] in canvas pixels.
 */
export function cameraViewportTrapezoid({
  mapW,
  mapH,
  size,
  x,
  y,
  farFrac = 0.26,
  nearFrac = 0.16,
  depthFrac = 0.14,
}) {
  const scaleX = size / mapW;
  const scaleY = size / mapH;
  const cx = x * scaleX;
  const cy = size - y * scaleY;
  const farHalf = (mapW * farFrac * scaleX) / 2;
  const nearHalf = (mapW * nearFrac * scaleX) / 2;
  const halfH = (mapH * depthFrac * scaleY) / 2;
  return [
    { x: cx - farHalf, y: cy - halfH },
    { x: cx + farHalf, y: cy - halfH },
    { x: cx + nearHalf, y: cy + halfH },
    { x: cx - nearHalf, y: cy + halfH },
  ];
}

/**
 * Axis-aligned world window for the Camera stage (D61 — no tilt).
 * Sized from the same FoV fractions the minimap trapezoid uses.
 * Returns { left, right, bottom, top } in map cells, clamped to the map.
 */
export function cameraWorldWindow({
  mapW,
  mapH,
  x,
  y,
  widthFrac = 0.26,
  heightFrac = 0.14,
  zoom = 1,
}) {
  const z = zoom > 0 ? zoom : 1;
  const halfW = (mapW * widthFrac) / (2 * z);
  const halfH = (mapH * heightFrac) / (2 * z);
  let left = x - halfW;
  let right = x + halfW;
  let bottom = y - halfH;
  let top = y + halfH;
  if (left < 0) {
    right -= left;
    left = 0;
  }
  if (bottom < 0) {
    top -= bottom;
    bottom = 0;
  }
  if (right > mapW) {
    left -= right - mapW;
    right = mapW;
  }
  if (top > mapH) {
    bottom -= top - mapH;
    top = mapH;
  }
  left = Math.max(0, left);
  bottom = Math.max(0, bottom);
  return { left, right, bottom, top };
}

/** Playhead speed steps for +/- keyboard control (D69). */
export const PLAYHEAD_SPEEDS = [1, 4, 8, 16, 32];

export function nextPlayheadSpeed(current, delta) {
  const idx = PLAYHEAD_SPEEDS.indexOf(current);
  const at = idx >= 0 ? idx : PLAYHEAD_SPEEDS.indexOf(8);
  const next = Math.max(0, Math.min(PLAYHEAD_SPEEDS.length - 1, at + delta));
  return PLAYHEAD_SPEEDS[next];
}

/** Camera zoom steps (buttons / wheel). */
export const CAMERA_ZOOMS = [0.5, 0.75, 1, 1.5, 2, 3];

export function nextCameraZoom(current, delta) {
  let best = 0;
  let bestDist = Infinity;
  for (let i = 0; i < CAMERA_ZOOMS.length; i += 1) {
    const d = Math.abs(CAMERA_ZOOMS[i] - current);
    if (d < bestDist) {
      bestDist = d;
      best = i;
    }
  }
  const next = Math.max(0, Math.min(CAMERA_ZOOMS.length - 1, best + delta));
  return CAMERA_ZOOMS[next];
}

/** Project a world (map-cell) point into Camera canvas pixels (Y flipped). */
export function worldToCameraCanvas(wx, wy, window, canvasW, canvasH) {
  const worldW = window.right - window.left;
  const worldH = window.top - window.bottom;
  if (worldW <= 0 || worldH <= 0) return { x: 0, y: 0 };
  return {
    x: ((wx - window.left) / worldW) * canvasW,
    y: ((window.top - wy) / worldH) * canvasH,
  };
}

/**
 * Integer cell-boundary lines inside the Camera window (D66).
 * Returns { vertical: number[], horizontal: number[] } in canvas pixels.
 */
export function cameraCellGridLines(window, canvasW, canvasH) {
  const worldW = window.right - window.left;
  const worldH = window.top - window.bottom;
  if (worldW <= 0 || worldH <= 0) return { vertical: [], horizontal: [] };
  const vertical = [];
  const horizontal = [];
  const x0 = Math.ceil(window.left);
  const x1 = Math.floor(window.right);
  for (let wx = x0; wx <= x1; wx += 1) {
    vertical.push(((wx - window.left) / worldW) * canvasW);
  }
  const y0 = Math.ceil(window.bottom);
  const y1 = Math.floor(window.top);
  for (let wy = y0; wy <= y1; wy += 1) {
    horizontal.push(((window.top - wy) / worldH) * canvasH);
  }
  return { vertical, horizontal };
}

/** Decode cliff_b64 (LE u16 row-major) from GET /api/map_geometry/{hash}. */
export function decodeCliffB64(cliffB64, width, height) {
  if (!cliffB64 || !width || !height) return null;
  const bin = atob(cliffB64);
  const n = width * height;
  if (bin.length < n * 2) return null;
  const cliff = new Uint16Array(n);
  for (let i = 0; i < n; i += 1) {
    const o = i * 2;
    cliff[i] = bin.charCodeAt(o) | (bin.charCodeAt(o + 1) << 8);
  }
  return cliff;
}

/** Losses with loss.t <= time. */
export function lossesAtTime(losses, time) {
  return (losses || []).filter((l) => (l.t || 0) <= time);
}

/**
 * Resolve selected player name → minimap player meta + build order.
 */
export function playerBundle(name, buildOrders, minimapPlayers) {
  const buildOrder =
    (buildOrders || []).find((bo) => bo.player === name) || null;
  const meta =
    (minimapPlayers || []).find((p) => p.name === name) ||
    (minimapPlayers || []).find(
      (p) => buildOrder?.player_analysis?.basic_info?.name === p.name
    ) ||
    null;
  return { buildOrder, meta };
}
